/**
 * Legal Video Service - Specialized service for legal consultation workflows
 * 
 * Extends the platform video service with legal-specific features:
 * - Attorney-client privilege handling
 * - Case-based room management
 * - Legal compliance features
 * - Billing integration
 */

import { 
  platformVideoService, 
  PlatformRoom, 
  PlatformToken, 
  CreateRoomRequest, 
  CreateTokenRequest,
  RoomStatus 
} from './platformVideoService';

// Legal-specific interfaces
export interface LegalConsultationRoom extends PlatformRoom {
  caseId?: string;
  clientName: string;
  attorneyName: string;
  firmName: string;
  caseType: string;
  urgency: 'high' | 'medium' | 'low';
  billableRate: number; // Per hour in dollars
  recordingConsent: boolean;
  attorneyClientPrivilege: boolean;
}

export interface LegalParticipant {
  token: string;
  roomId: string;
  participantId: string;
  name: string;
  role: 'attorney' | 'client' | 'observer';
  permissions: {
    canRecord: boolean;
    canTakeNotes: boolean;
    canUploadDocuments: boolean;
    canScheduleFollowUp: boolean;
  };
}

export interface CreateLegalConsultationRequest {
  clientName: string;
  attorneyName: string;
  firmName: string;
  caseType: string;
  caseId?: string;
  urgency?: 'high' | 'medium' | 'low';
  billableRate?: number;
  estimatedDuration?: number; // minutes
  recordingRequested?: boolean;
  metadata?: {
    intakeComplete?: boolean;
    documentsUploaded?: string[];
    clientNotes?: string;
    urgencyReason?: string;
  };
}

export interface LegalConsultationStatus extends RoomStatus {
  billableTime: number; // seconds
  billableAmount: number; // dollars
  caseNotes?: string;
  recordingStatus: {
    enabled: boolean;
    consentGiven: boolean;
    recordingActive: boolean;
    duration: number;
  };
  clientPresent: boolean;
  attorneyPresent: boolean;
}

export class LegalVideoService {
  private readonly LEGAL_ORG_ID = 'legal_template_demo'; // Demo organization for templates
  private readonly DEFAULT_BILLABLE_RATE = 350; // $350/hour

  /**
   * Create a legal consultation room
   */
  async createConsultation(request: CreateLegalConsultationRequest): Promise<LegalConsultationRoom> {
    try {
      console.log('[LegalVideo] Creating consultation room for:', request.clientName);

      const roomRequest: CreateRoomRequest = {
        name: `Legal Consultation - ${request.clientName} vs ${request.caseType}`,
        maxParticipants: 5, // Attorney, client, observers
        recordingEnabled: request.recordingRequested || false,
        expiresIn: (request.estimatedDuration || 60) * 60, // Convert minutes to seconds
        metadata: {
          purpose: 'legal_consultation',
          caseType: request.caseType,
          clientName: request.clientName,
          attorneyName: request.attorneyName,
          firmName: request.firmName,
          urgency: request.urgency || 'medium',
          billableRate: request.billableRate || this.DEFAULT_BILLABLE_RATE,
          recordingConsent: false, // Will be set when client provides consent
          attorneyClientPrivilege: true,
          ...request.metadata
        }
      };

      const room = await platformVideoService.createRoom(this.LEGAL_ORG_ID, roomRequest);

      // Transform to legal consultation room
      const legalRoom: LegalConsultationRoom = {
        ...room,
        caseId: request.caseId,
        clientName: request.clientName,
        attorneyName: request.attorneyName,
        firmName: request.firmName,
        caseType: request.caseType,
        urgency: request.urgency || 'medium',
        billableRate: request.billableRate || this.DEFAULT_BILLABLE_RATE,
        recordingConsent: request.recordingRequested || false,
        attorneyClientPrivilege: true
      };

      console.log('[LegalVideo] Created consultation room:', legalRoom.roomId);
      return legalRoom;

    } catch (error) {
      console.error('[LegalVideo] Error creating consultation:', error);
      throw new Error(`Failed to create legal consultation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate attorney join token
   */
  async createAttorneyToken(
    roomId: string, 
    attorneyName: string,
    permissions?: Partial<LegalParticipant['permissions']>
  ): Promise<LegalParticipant> {
    try {
      const tokenRequest: CreateTokenRequest = {
        participantName: attorneyName,
        role: 'host', // Attorneys are hosts
        permissions: {
          canPublishVideo: true,
          canPublishAudio: true,
          canScreenShare: true,
          canRecord: true
        },
        metadata: {
          participantType: 'attorney',
          legalRole: 'counsel',
          privilegeLevel: 'attorney-client'
        }
      };

      const token = await platformVideoService.createToken(roomId, this.LEGAL_ORG_ID, tokenRequest);

      return {
        token: token.token,
        roomId: token.roomId,
        participantId: token.participantId,
        name: attorneyName,
        role: 'attorney',
        permissions: {
          canRecord: true,
          canTakeNotes: true,
          canUploadDocuments: true,
          canScheduleFollowUp: true,
          ...permissions
        }
      };

    } catch (error) {
      console.error('[LegalVideo] Error creating attorney token:', error);
      throw new Error(`Failed to create attorney token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate client join token
   */
  async createClientToken(
    roomId: string, 
    clientName: string,
    permissions?: Partial<LegalParticipant['permissions']>
  ): Promise<LegalParticipant> {
    try {
      const tokenRequest: CreateTokenRequest = {
        participantName: clientName,
        role: 'participant', // Clients are participants
        permissions: {
          canPublishVideo: true,
          canPublishAudio: true,
          canScreenShare: false, // Clients typically can't screen share
          canRecord: false // Only attorney can control recording
        },
        metadata: {
          participantType: 'client',
          legalRole: 'client',
          privilegeLevel: 'attorney-client'
        }
      };

      const token = await platformVideoService.createToken(roomId, this.LEGAL_ORG_ID, tokenRequest);

      return {
        token: token.token,
        roomId: token.roomId,
        participantId: token.participantId,
        name: clientName,
        role: 'client',
        permissions: {
          canRecord: false,
          canTakeNotes: false,
          canUploadDocuments: true,
          canScheduleFollowUp: false,
          ...permissions
        }
      };

    } catch (error) {
      console.error('[LegalVideo] Error creating client token:', error);
      throw new Error(`Failed to create client token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get enhanced consultation status with legal-specific data
   */
  async getConsultationStatus(roomId: string): Promise<LegalConsultationStatus> {
    try {
      const status = await platformVideoService.getRoomStatus(roomId, this.LEGAL_ORG_ID);
      
      // Calculate billable information
      const billableRate = 350; // Default rate, should come from room metadata
      const billableTimeHours = status.duration / 3600;
      const billableAmount = billableTimeHours * billableRate;

      // Check participant presence
      const participants = status.participants.list;
      const attorneyPresent = participants.some(p => p.role === 'attorney' || p.role === 'host');
      const clientPresent = participants.some(p => p.role === 'client' || p.role === 'participant');

      const legalStatus: LegalConsultationStatus = {
        ...status,
        billableTime: status.duration,
        billableAmount: Math.round(billableAmount * 100) / 100, // Round to 2 decimal places
        recordingStatus: {
          enabled: status.recording?.enabled || false,
          consentGiven: false, // TODO: Get from room metadata
          recordingActive: status.recording?.status === 'recording',
          duration: status.recording?.duration || 0
        },
        clientPresent,
        attorneyPresent
      };

      return legalStatus;

    } catch (error) {
      console.error('[LegalVideo] Error getting consultation status:', error);
      throw new Error(`Failed to get consultation status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * End consultation and generate billing summary
   */
  async endConsultation(roomId: string): Promise<{
    success: boolean;
    endedAt: string;
    billingSummary: {
      duration: number; // seconds
      billableAmount: number;
      rate: number;
    };
  }> {
    try {
      // Get final status for billing
      const finalStatus = await this.getConsultationStatus(roomId);
      
      // End the room
      const result = await platformVideoService.endRoom(roomId, this.LEGAL_ORG_ID);

      return {
        ...result,
        billingSummary: {
          duration: finalStatus.billableTime,
          billableAmount: finalStatus.billableAmount,
          rate: this.DEFAULT_BILLABLE_RATE
        }
      };

    } catch (error) {
      console.error('[LegalVideo] Error ending consultation:', error);
      throw new Error(`Failed to end consultation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update recording consent status
   */
  async updateRecordingConsent(roomId: string, consentGiven: boolean): Promise<void> {
    try {
      // TODO: Update room metadata with consent status
      // This would typically be done through a PATCH endpoint
      console.log('[LegalVideo] Recording consent updated:', { roomId, consentGiven });
      
      // For now, we'll store this locally or in the consultation state
      // In a full implementation, this would update the room metadata
      
    } catch (error) {
      console.error('[LegalVideo] Error updating recording consent:', error);
      throw new Error(`Failed to update recording consent: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * List active consultations for a firm
   */
  async listActiveConsultations(firmName?: string): Promise<LegalConsultationRoom[]> {
    try {
      const result = await platformVideoService.listRooms(this.LEGAL_ORG_ID, 'active');
      
      // Transform to legal consultation rooms and filter by firm if specified
      const consultations = result.rooms
        .filter(room => room.metadata?.purpose === 'legal_consultation')
        .filter(room => !firmName || (room.metadata as any)?.firmName === firmName)
        .map(room => this.transformToLegalRoom(room));

      return consultations;

    } catch (error) {
      console.error('[LegalVideo] Error listing consultations:', error);
      throw new Error(`Failed to list consultations: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Transform platform room to legal consultation room
   */
  private transformToLegalRoom(room: PlatformRoom): LegalConsultationRoom {
    const metadata = room.metadata as any || {};
    
    return {
      ...room,
      caseId: metadata.caseId,
      clientName: metadata.clientName || 'Unknown Client',
      attorneyName: metadata.attorneyName || 'Unknown Attorney',
      firmName: metadata.firmName || 'Unknown Firm',
      caseType: metadata.caseType || 'General Legal Matter',
      urgency: metadata.urgency || 'medium',
      billableRate: metadata.billableRate || this.DEFAULT_BILLABLE_RATE,
      recordingConsent: metadata.recordingConsent || false,
      attorneyClientPrivilege: metadata.attorneyClientPrivilege !== false
    };
  }

  /**
   * Generate a shareable consultation link for clients
   */
  generateClientLink(roomId: string, clientName: string, requestId?: string): string {
    // In a real implementation, this would generate a secure, time-limited link
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      roomId,
      clientName,
      type: 'legal_client'
    });
    
    if (requestId) {
      params.set('requestId', requestId);
    }
    
    // Use new separate client route
    return `${baseUrl}/templates/legal/consultation/client?${params.toString()}`;
  }

  /**
   * Generate attorney dashboard link
   */
  generateAttorneyLink(roomId: string, attorneyName: string, requestId?: string): string {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      roomId,
      attorneyName,
      type: 'legal_attorney'
    });
    
    if (requestId) {
      params.set('requestId', requestId);
    }
    
    // Use new separate attorney route
    return `${baseUrl}/templates/legal/consultation/attorney?${params.toString()}`;
  }
}

// Export singleton instance
export const legalVideoService = new LegalVideoService();
export default legalVideoService;